import { useState } from "react";
import type { OperationMatrix } from "../domain/engine";
import type { FailureCase } from "../domain/failure-rules";
import { CaseEditor } from "./CaseEditor";

interface MatrixTableProps {
  matrix: OperationMatrix[];
  onOverride: (caseId: string, patch: Partial<Pick<FailureCase, "justification" | "accepted" | "reviewed">>) => void;
}

function severityBadgeClass(severity: FailureCase["severity"]): string {
  return `state-badge state-badge--${severity}`;
}

function originLabel(failureCase: FailureCase): string {
  if (failureCase.documented) return "Documentado en el contrato";
  if (failureCase.assumption) return "Supuesto del motor";
  return "Inferido";
}

export function MatrixTable({ matrix, onOverride }: MatrixTableProps) {
  const [openCaseId, setOpenCaseId] = useState<string | null>(null);

  if (matrix.length === 0) {
    return (
      <div className="empty-state">
        <p>Aún no hay resultado. Ejecuta el fixture para ver cada decisión.</p>
      </div>
    );
  }

  return (
    <div>
      {matrix.map((operation) => (
        <details className="operation-card" key={operation.operationId} open={matrix.length <= 3}>
          <summary>
            <span className="method">{operation.method.toUpperCase()}</span>
            <span>{operation.path}</span>
            <span className="field-hint">
              {operation.security.length > 0
                ? `Seguridad: ${operation.security.map((s) => s.schemeName).join(", ")}`
                : "Sin seguridad declarada"}
            </span>
          </summary>
          <div className="card-body">
            {operation.summary ? <p>{operation.summary}</p> : null}
            <ul className="case-list">
              {operation.cases.map((failureCase) => {
                const example = operation.testExamples.find((candidate) => candidate.caseId === failureCase.id);
                const isOpen = openCaseId === failureCase.id;
                return (
                  <li className="case-row" key={failureCase.id}>
                    <div className="case-row-header">
                      <span className="status-code">{failureCase.statusCode}</span>
                      <span>{failureCase.title}</span>
                      <span className={severityBadgeClass(failureCase.severity)}>{failureCase.severity}</span>
                      <span className="state-badge state-badge--info">{originLabel(failureCase)}</span>
                      {failureCase.accepted ? (
                        <span className={`state-badge ${failureCase.reviewed ? "state-badge--success" : "state-badge--warning"}`}>
                          {failureCase.reviewed ? "Revisado" : "Sin revisar"}
                        </span>
                      ) : (
                        <span className="state-badge state-badge--critical">No aplica</span>
                      )}
                      <button
                        type="button"
                        className="button button-secondary button-small"
                        onClick={() => setOpenCaseId(isOpen ? null : failureCase.id)}
                        aria-expanded={isOpen}
                      >
                        {isOpen ? "Cerrar" : "Editar / justificar"}
                      </button>
                    </div>
                    <p className="rationale">
                      {failureCase.rationale} <code>{failureCase.evidencePath}</code>
                    </p>
                    <p className="field-hint">Justificación actual: {failureCase.justification}</p>
                    {example ? (
                      <details>
                        <summary>Ejemplo de prueba ({example.expectedStatus})</summary>
                        <pre className="example">{example.curl}</pre>
                      </details>
                    ) : null}
                    {isOpen ? (
                      <CaseEditor
                        failureCase={failureCase}
                        onSave={(patch) => onOverride(failureCase.id, patch)}
                        onClose={() => setOpenCaseId(null)}
                      />
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        </details>
      ))}
    </div>
  );
}
